// Feeds the open field's soil types to the legend.
//
// Two deliberate constraints:
//
// 1. It only fetches when the soil layer is actually switched on. The legend is
//    the only consumer, so fetching for a farmer who never opens soil would add
//    a request to zone-open, which is the path that already carries a measured
//    1 to 8 second freeze.
// 2. It goes through `getZoneLandData`, which is memoised on zone id plus a hash
//    of the boundary. The Zone Details panel calls the same function, so when
//    both are on screen this costs no second request, and an edited boundary
//    invalidates both together.

import { useEffect, useRef, useState } from 'react';
import { getZoneLandData, getZoneIdentity } from './zoneLandData';

/**
 * Replace the state only when it actually changes.
 *
 * Reference stability is load-bearing here rather than an optimisation. The
 * effect below depends on the zone OBJECT, which the parent rebuilds on every
 * render, and `getZoneLandData` hands back the same memoised promise resolving
 * to the same object. If a settled fetch always produced a NEW state object,
 * every resolve would re-render, the re-render would re-run the effect, and the
 * effect would settle again: a loop. Handing `prev` straight back is what makes
 * React bail out, and it is the reason `status` is not a second `useState`.
 */
function settle(next) {
  return (prev) =>
    prev.data === next.data && prev.status === next.status ? prev : next;
}

const IDLE = { data: null, status: 'idle' };

/**
 * @param {object|null} zone the open zone document, or null when none is open
 * @param {boolean} enabled whether the soil layer is switched on
 * @returns {{data: object|null, status: 'idle'|'loading'|'loaded'|'unavailable'}}
 *   `status` exists because `data: null` alone cannot tell "no field is open"
 *   apart from "we are still asking" and "the ask failed", and the legend has to
 *   say something different in each case. Before it existed, all three rendered
 *   "Open a field to see the soil types in it." to a farmer who had one open.
 */
export function useZoneSoilLegend(zone, enabled) {
  const [state, setState] = useState(IDLE);
  // Which field the data in state belongs to, so a field CHANGE can be told
  // apart from a re-fetch of the same field. Not the raw id: a zone carrying no
  // id at all yields null, and two ID-less fields would then compare equal and
  // defeat the clear below. `getZoneIdentity` covers the id AND the boundary, so
  // an edited boundary is a different field here, which it has to be: with one
  // ask per field, keying on the id alone would leave the old shape's soil
  // classes on screen for the new shape and never re-ask. It is the same
  // function getZoneLandData keys its cache on, so the two cannot disagree.
  const shownZoneKey = useRef(null);
  // Which field we have already published a FINAL answer for, resolved or
  // rejected. One ask per field, and the answer stands until the field changes.
  //
  // This effect re-runs on every parent render, because it depends on the zone
  // OBJECT and the parent rebuilds it each time. It used to ask again on every
  // one of those runs, which looked free and was not:
  //
  //   On a FAILURE it was not free at all. getZoneLandData deliberately does not
  //   cache a rejection (zoneLandData/index.js:245), so a soil endpoint that was
  //   down got a fresh request on every render of the map for as long as the
  //   field stayed open. Nothing surfaced it while the failure was invisible,
  //   because setting null over null made React bail out and nothing re-ran.
  //   Publishing 'unavailable' is a real change, so it re-renders, so it re-runs.
  //
  //   On a SUCCESS it was usually a cache hit, but only usually: that cache is
  //   capped at CACHE_MAX_ENTRIES with oldest-first eviction, so a farmer who has
  //   opened enough fields evicts the entry and the next render is a real
  //   request again. CodeRabbit raised this on PR #24 and it is right.
  //
  // The farmer reopening the field, or editing its boundary, is the retry.
  const answeredKey = useRef(null);

  const hasBoundary = Boolean(zone?.polygon_shape?.coordinates?.length);
  const zoneKey = getZoneIdentity(zone);

  useEffect(() => {
    if (!enabled || !hasBoundary) {
      setState(settle(IDLE));
      shownZoneKey.current = null;
      answeredKey.current = null;
      return undefined;
    }
    let cancelled = false;
    // Deliberately not cleared on a re-fetch of the SAME field: the previous
    // classes stay on screen rather than the card flicking back to "open a
    // field", which would read as the field having been closed.
    //
    // But it must be cleared when a DIFFERENT field opens. Holding the old data
    // there meant field A's soil classes stayed on screen while field B loaded,
    // labelled as field B's — telling a farmer their field is something it is
    // not. A blank moment is the honest answer while we do not know yet.
    if (shownZoneKey.current !== zoneKey) {
      shownZoneKey.current = zoneKey;
      answeredKey.current = null;
      setState(settle({ data: null, status: 'loading' }));
    } else if (answeredKey.current === zoneKey) {
      // This field is answered. Leave the answer on screen and do not ask again.
      return undefined;
    } else {
      // Same field, nothing final published yet, so we are genuinely still
      // asking and the legend has to say so rather than fall through to the
      // no-field prompt. Guarded on the status so a re-render does not churn it.
      setState((prev) => (prev.status === 'loading' ? prev : { data: null, status: 'loading' }));
    }
    getZoneLandData(zone)
      .then((result) => {
        if (cancelled) return;
        const usable = result && result.status !== 'no-polygon' ? result : null;
        // 'loaded' even when `usable` is null: the question has been answered,
        // and the answer is that this field has no soil information. The legend
        // says exactly that, which is not the same as "open a field".
        answeredKey.current = zoneKey;
        setState(settle({ data: usable, status: 'loaded' }));
      })
      .catch(() => {
        // Per-layer failures are already handled inside getZoneLandData, so
        // reaching here means the whole fetch failed.
        //
        // This used to fall back to the card's no-field prompt and call that
        // honest. It was not: "Open a field to see the soil types in it" tells a
        // farmer who HAS one open that they have not, which is the same wrong
        // conclusion BBB-527 exists to prevent, in a different card. It now
        // reports 'unavailable' and the legend says "Not available right now".
        if (cancelled) return;
        answeredKey.current = zoneKey;
        setState(settle({ data: null, status: 'unavailable' }));
      });
    return () => {
      cancelled = true;
    };
    // Depends on the zone OBJECT, not its id. The parent rebuilds `zones` on
    // render, so this effect re-runs often, but `getZoneLandData` is memoised on
    // zone id plus a hash of the boundary and hands back the SAME promise, which
    // resolves to the same object, so `settle` hands back the previous state
    // unchanged and React bails out. No request, no re-render, no loop.
    //
    // Keying on the id instead would be the bug it looks like a fix for: a
    // farmer editing a field boundary keeps the same id, so the effect would
    // never re-run and the legend would show the old field's soil types.
    //
    // `zoneKey` is listed too, but only as the thing the clear-on-field-change
    // check reads. It is derived from `zone`, so it never causes a re-run the
    // zone object did not already cause. It is deliberately NOT the trigger.
  }, [zone, zoneKey, hasBoundary, enabled]);

  return state;
}
